import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { NotificationService } from '../../services/notification.service';
import { LoadingService } from '../../services/loading.service';

@Component({
  selector: 'app-simple-enquiry',
  templateUrl: './simple-enquiry.component.html',
  styleUrls: ['./simple-enquiry.component.scss']
})
export class SimpleEnquiryComponent implements OnInit {
  locations: any[] = [];
  selectedLocation: string = '';
  regNo: string = '';

  // Image Upload State
  uploadedImages: any[] = [];
  uploadedVoiceUrl: string = '';
  previewImageUrl: string | null = null;
  readonly MAX_IMAGES = 5;

  // Voice Recording State
  @ViewChild('existingAudioInput') existingAudioInput!: ElementRef;
  isRecording = false;
  isPlayingExample = false;
  isPlayingRecording = false;
  isAudioConfirmed = false;
  recordedAudioBlob: Blob | null = null;
  recordedAudioUrl: string | null = null;
  private mediaRecorder: MediaRecorder | null = null;

  get recordedAudioName(): string {
    if (this.recordedAudioBlob && (this.recordedAudioBlob as any).name) {
      return (this.recordedAudioBlob as any).name;
    }
    return '';
  }
  private audioChunks: Blob[] = [];
  private exampleAudio: HTMLAudioElement | null = null;
  private recordedAudio: HTMLAudioElement | null = null;
  isUploadingImages = false;
  isUploadingVoice = false;
  isSubmitting = false;

  constructor(
    public router: Router,
    private http: ApiService,
    private notify: NotificationService,
    private loadingService: LoadingService
  ) {}

  ngOnInit(): void {
    this.getLocations();
  }

  getLocations() {
    this.http.get('retailers/getLocationsByAuthId').subscribe((res: any) => {
      const responseObj = res.response ? res.response : res;
      if (responseObj && responseObj.code === 1 && responseObj.data && responseObj.data.records) {
        this.locations = responseObj.data.records;
        if (this.locations.length > 0) {
          this.selectedLocation = this.locations[0].branchId;
        }
      } else {
        this.locations = [];
      }
    });
  }

  // --- Image Upload Logic ---
  onImageSelected(event: any) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (this.uploadedImages.length + files.length > this.MAX_IMAGES) {
      this.notify.showError(`Maximum ${this.MAX_IMAGES} images allowed.`, '');
      return;
    }

    this.isUploadingImages = true;
    let completed = 0;
    for (let i = 0; i < files.length; i++) {
      const formData = new FormData();
      formData.append('image', files[i]);

      this.http.filePost('retailers/enquiryNoteFilesUpload', formData).subscribe(
        (res: any) => {
          completed++;
          const responseObj = res.response ? res.response : res;
          if (responseObj && responseObj.code === 1 && responseObj.data) {
            if (responseObj.data.img_url) {
              this.uploadedImages.push({ img_url: responseObj.data.img_url });
            } else if (responseObj.data.img_error) {
              this.notify.showError(responseObj.data.img_error, '');
            }
          } else {
            this.notify.showError(responseObj?.message || 'Image upload failed', '');
          }

          if (completed === files.length) {
            this.isUploadingImages = false;
          }
        },
        (error) => {
          completed++;
          this.notify.showError('Error uploading image', '');
          if (completed === files.length) {
            this.isUploadingImages = false;
          }
        }
      );
    }
    // reset input
    event.target.value = '';
  }

  removeImage(index: number) {
    this.uploadedImages.splice(index, 1);
  }

  openImagePreview(url: string) {
    this.previewImageUrl = url;
  }

  closeImagePreview() {
    this.previewImageUrl = null;
  }

  // --- VOICE LOGIC ---
  playExampleVoice(event: Event) {
    event.stopPropagation();
    const audioPath = '/assets/audio/voice-example.mp3';

    if (this.exampleAudio) {
      this.exampleAudio.pause();
      this.exampleAudio.currentTime = 0;
    } else {
      this.exampleAudio = new Audio(audioPath);
      this.exampleAudio.onended = () => {
        this.isPlayingExample = false;
      };
    }

    this.exampleAudio.play().catch(e => {
      console.error('Error playing example audio', e);
      this.notify.showError('Could not play example audio', '');
    });
    this.isPlayingExample = true;
  }

  async startVehicleRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaRecorder = new MediaRecorder(stream);
      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        this.recordedAudioBlob = new Blob(this.audioChunks, { type: 'audio/m4a' });
        this.isAudioConfirmed = false;
        this.uploadedVoiceUrl = '';
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };

      this.mediaRecorder.start();
      this.isRecording = true;
    } catch (error) {
      console.error('Error accessing microphone:', error);
      this.notify.showError('Microphone access denied or not supported', '');
    }
  }

  stopVehicleRecording() {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop();
      this.isRecording = false;
    }
  }

  playRecordedAudio() {
    if (!this.recordedAudioBlob) return;

    if (!this.recordedAudio) {
      this.recordedAudioUrl = URL.createObjectURL(this.recordedAudioBlob);
      this.recordedAudio = new Audio(this.recordedAudioUrl);
      this.recordedAudio.onended = () => {
        this.isPlayingRecording = false;
      };
    }

    if (this.isPlayingRecording) {
      this.recordedAudio.pause();
      this.isPlayingRecording = false;
    } else {
      this.recordedAudio.play().catch(e => {
        console.error('Error playing recording', e);
        this.notify.showError('Could not play recording', '');
      });
      this.isPlayingRecording = true;
    }
  }

  confirmAudio() {
    if (!this.recordedAudioBlob) return;
    
    this.isUploadingVoice = true;
    const formData = new FormData();
    if (typeof this.recordedAudioBlob === 'object' && (this.recordedAudioBlob as any).name) {
      const audioFile: any = this.recordedAudioBlob;
      const fileName = audioFile.name || 'voice.m4a';
      formData.append('voice', audioFile, fileName);
    } else {
      const isM4a = this.recordedAudioBlob.type === 'audio/aac' || this.recordedAudioBlob.type === 'audio/mp4';
      const ext = isM4a ? 'voice.m4a' : 'voice.webm';
      formData.append('voice', this.recordedAudioBlob, ext);
    }

    this.http.filePost('retailers/enquiryNoteFilesUpload', formData).subscribe(
      (res: any) => {
        this.isUploadingVoice = false;
        const responseObj = res.response ? res.response : res;
        if (responseObj && responseObj.code === 1 && responseObj.data && responseObj.data.voice_url) {
          this.uploadedVoiceUrl = responseObj.data.voice_url;
          this.isAudioConfirmed = true;
          this.notify.showSuccess('Voice confirmed and uploaded', '');
        } else {
          this.notify.showError(responseObj?.message || 'Voice upload failed', '');
        }
      },
      (error) => {
        this.isUploadingVoice = false;
        this.notify.showError('Error uploading voice', '');
      }
    );
  }

  deleteRecording() {
    this.recordedAudioBlob = null;
    this.isAudioConfirmed = false;
    this.uploadedVoiceUrl = '';
    if (this.recordedAudioUrl) {
      URL.revokeObjectURL(this.recordedAudioUrl);
      this.recordedAudioUrl = null;
    }
    if (this.recordedAudio) {
      this.recordedAudio.pause();
      this.recordedAudio = null;
    }
    this.isPlayingRecording = false;
  }

  onExistingAudioSelected(event: any) {
    const target = event.target as HTMLInputElement;
    const file = target.files && target.files.length > 0 ? target.files[0] : null;

    if (!file) {
      return;
    }

    const fileName = (file.name || '').toLowerCase();
    
    // We will warn but not block if the extension isn't in our known list,
    // to prevent blocking valid files that the OS miscategorizes.
    const supportedAudioExtensions = [
      '.webm', '.mp3', '.wav', '.m4a', '.aac', '.ogg', '.oga', '.opus', '.amr', '.3gp', '.mp4'
    ];
    
    const hasAudioMime = !!file.type && (file.type.startsWith('audio/') || file.type.startsWith('video/'));
    const hasSupportedExtension = supportedAudioExtensions.some(ext => fileName.endsWith(ext));

    if (!hasAudioMime && !hasSupportedExtension) {
      this.notify.showError(`Warning: Unknown audio type (${file.type || 'none'}). Attempting to use anyway.`, '');
    }

    if (this.recordedAudio) {
      this.recordedAudio.pause();
      this.recordedAudio = null;
    }
    this.isPlayingRecording = false;

    if (this.recordedAudioUrl) {
      URL.revokeObjectURL(this.recordedAudioUrl);
      this.recordedAudioUrl = null;
    }

    this.recordedAudioBlob = file;
    this.isAudioConfirmed = false;
    this.uploadedVoiceUrl = '';
    this.notify.showSuccess(`Audio file selected: ${file.name}`, '');
    target.value = '';
  }

  // --- Form Submission Logic ---
  onSubmit() {
    console.log("Submit triggered. Selected location:", this.selectedLocation);
    if (!this.selectedLocation) {
      this.notify.showError('Please select a location.', '');
      return;
    }

    console.log("Uploaded images:", this.uploadedImages.length, "Voice URL:", this.uploadedVoiceUrl);
    if (this.uploadedImages.length === 0 && !this.uploadedVoiceUrl) {
      this.notify.showError('Please upload at least one image or a voice recording.', '');
      return;
    }

    this.isSubmitting = true;

    const payload = {
      branch_id: this.selectedLocation,
      text: this.regNo,
      voice_url: this.uploadedVoiceUrl,
      images: this.uploadedImages
    };
    console.log("Submitting payload:", payload);

    this.http.post('retailers/saveEnquiryNote', payload).subscribe(
      (res: any) => {
        this.isSubmitting = false;
        if (res.code === 1) {
          this.notify.showSuccess(res.message, '');
          this.router.navigate(['/dashboard']);
        } else {
          this.notify.showError(res.message, '');
        }
      },
      (error) => {
        this.isSubmitting = false;
        this.notify.showError('An error occurred while saving. Please try again.', '');
      }
    );
  }
}
